import { AitHeaderCellData, AitHeaderGroupData, AitLocationHeaderCell } from "./interface";
import { trGetCellFromLocation } from "./trGetCellFromLocation";

export interface trCanRemoveRowSpanReturn<T> {
  canRemove: true;
  reasonNot: undefined;
  targetCell: AitHeaderCellData<T>;
  showCell: AitHeaderCellData<T>;
}

export interface trCanNotRemoveRowSpanReturn<T> {
  canRemove: false;
  reasonNot: string;
  targetCell: AitHeaderCellData<T> | undefined;
  showCell: AitHeaderCellData<T> | undefined;
}

/**
 * Determine whether a row span can be removed at the specified header cell.
 *
 * @template T - The header cell payload type
 * @param headerData - Header group data to inspect
 * @param location - Location describing the target header cell
 * @param newRowSpan - Desired new rowSpan value (must be >= 1)
 * @returns Either a success object (`canRemove: true`) containing `targetCell` and the `showCell` to reveal,
 *          or a failure object (`canRemove: false`) with `reasonNot` explaining why removal is not permitted.
 */
export const trCanRemoveRowSpan = <T>(
  headerData: AitHeaderGroupData<T>,
  location: AitLocationHeaderCell,
  newRowSpan: number,
): trCanRemoveRowSpanReturn<T> | trCanNotRemoveRowSpanReturn<T> => {
  let reasonNot = "";
  let showCell: AitHeaderCellData<T> | undefined;

  // Check target cell exists
  const targetCell = trGetCellFromLocation(
    {
      headerData: headerData,
      bodyData: [],
      coreTrafficLight: "BlankNotEvaluated",
      columnInformation: [],
    },
    location,
    true,
  ) as AitHeaderCellData<T> | undefined;
  if (targetCell === undefined) {
    reasonNot = "RemoveRowSpan: target cell not found";
  } else if (targetCell.rowSpan === newRowSpan) {
    reasonNot = "";
  } else if (location.isRepeat) {
    reasonNot = "RemoveRowSpan: this is a repeat cell";
  } else {
    // Calculated new row span
    const oldRowSpan = targetCell.rowSpan;
    if (newRowSpan < 1) {
      reasonNot = "RemoveRowSpan: row span cannot be reduced";
    } else {
      // Get the cell to show
      showCell = headerData.rows[location.row + oldRowSpan - 1]?.cells[location.originalColumn];
      if (showCell === undefined) {
        reasonNot = "RemoveRowSpan: show cell does not exist";
      } else if (showCell.rowSpan !== 0) {
        reasonNot = "RemoveRowSpan: show cell is not hidden";
      }
    }
  }

  return reasonNot !== ""
    ? { canRemove: false, reasonNot, targetCell, showCell: showCell }
    : {
        canRemove: true,
        reasonNot: undefined,
        targetCell: targetCell!,
        showCell: showCell!,
      };
};

/**
 * Apply a row-span removal to `headerData`.
 *
 * This sets the `rowSpan` of the target cell to `newSpan` and will set the
 * previously hidden `showCell` to `rowSpan: 1`. Use `trCanRemoveRowSpan` to
 * validate the operation before calling to avoid exceptions.
 *
 * @template T - The header cell payload type
 * @param headerData - Header group data to modify
 * @param location - Location describing the cell to change
 * @param newSpan - New rowSpan value to set
 * @returns An updated `AitHeaderGroupData<T>` with row-span changes applied
 * @throws Error when removal is not permitted (see `trCanRemoveRowSpan` for reasons)
 */
export const trRemoveRowSpan = <T>(
  headerData: AitHeaderGroupData<T>,
  location: AitLocationHeaderCell,
  newSpan: number,
) => {
  const { canRemove, reasonNot, targetCell, showCell } = trCanRemoveRowSpan(
    headerData,
    location,
    newSpan,
  );

  // Already updated
  if (targetCell?.rowSpan === newSpan) return;

  // Cannot remove
  if (!canRemove) throw new Error(reasonNot);

  // Create return object
  const newHeaderData = { ...headerData };
  newHeaderData.rows = headerData.rows.map((row) => {
    if (row.aitid !== location.rowId && !row.cells.find((c) => c.aitid === showCell.aitid))
      return row;
    const newCells = row.cells.map((cell) => {
      if (cell.aitid === targetCell.aitid) {
        return {
          ...cell,
          rowSpan: newSpan,
        };
      }
      if (cell.aitid === showCell.aitid) {
        return {
          ...cell,
          rowSpan: 1,
        };
      }
      return cell;
    });
    return { ...row, cells: newCells };
  });
  return newHeaderData;
};
